import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { Pass, FullScreenQuad } from "three/examples/jsm/postprocessing/Pass.js";
import { GTAOPass } from "three/examples/jsm/postprocessing/GTAOPass.js";
import { BloomMips } from "./bloomMips";
import type { Look } from "./look";
import { CHEST_TONE } from "./look";
import { screenTint } from "./screenTint";

/**
 * The post chain: scene (4× MSAA on the tiers that can afford it) → (ground-truth AO) → bloom mips → ONE final pass.
 *
 * Bloom makes the `_glow` slots and the additive VFX read as light; AO seats the models on the ground and darkens the
 * creases the baked maps cannot; the final pass is where the "look" lives. It used to be four full-screen passes after
 * the scene — bloom's own additive blend, a grade pass, the OutputPass — each a read and a write of a half-float
 * screen. The final pass now does all of it in one read (lighting review F6, ~1 ms back on a phone):
 *
 *   scene + bloom → (FXAA, medium tiers) → grade (split tone, saturation, contrast, lift) → vignette → exposure +
 *   tone map → (3D LUT hook) → sRGB
 *
 * `transparent` (the reward chest canvas) makes the bloom keep alpha.
 *
 * The canvas has no MSAA: every pixel on it is written by the final quad. `scale` renders the scene target below the
 * canvas resolution — the governor's dynamic resolution (quality.ts) — and the final pass upsamples it.
 */
export class PostFx {
  private readonly composer: EffectComposer;
  private readonly bloom: BloomMips;
  private readonly final: FinalPass;
  private ao: GTAOPass | null = null;
  /** MSAA sample count of the scene target (0 = none); fixed for this PostFx's life (SceneRig rebuilds to change it). */
  readonly samples: number;

  constructor(private readonly renderer: THREE.WebGLRenderer, private readonly scene: THREE.Scene, private readonly camera: THREE.Camera, width: number, height: number, strength = 0.55, transparent = false, samples = 0, fxaa = false) {
    // Lighting review F3: three r169's EffectComposer allocates its targets with `samples: 0`, so with post on — every
    // tier with bloom — the scene had NO antialiasing at all; the canvas's own `antialias: true` only multisampled the
    // final full-screen quad (and still paid for a 4× backbuffer). The scene target is multisampled instead. On Apple
    // GPUs 4× MSAA resolves on tile and is the cheap AA: far cheaper than rendering at 2× instead of 1.5×.
    this.samples = Math.max(0, Math.min(samples, PostFx.maxHalfFloatSamples(renderer)));
    const dpr = renderer.getPixelRatio();
    const rt = new THREE.WebGLRenderTarget(Math.max(1, width * dpr), Math.max(1, height * dpr), { type: THREE.HalfFloatType, samples: this.samples });
    rt.texture.name = "PostFx.scene";
    this.composer = new EffectComposer(renderer, rt);
    this.composer.addPass(new RenderPass(scene, camera));
    this.bloom = new BloomMips(strength, 0.4, 0.86);
    if (transparent) {
      // The bloom blur writes alpha 1 everywhere, which turns a transparent canvas (the reward chest over the parchment)
      // black. Let the bloom's alpha follow its brightness so empty areas stay clear.
      const m = (this.bloom as unknown as { compositeMaterial: THREE.ShaderMaterial }).compositeMaterial;
      m.fragmentShader = m.fragmentShader.replace(/\}\s*$/, "gl_FragColor.a = clamp(max(gl_FragColor.r, max(gl_FragColor.g, gl_FragColor.b)), 0.0, 1.0); }");
      m.needsUpdate = true;
    }
    this.composer.addPass(this.bloom);
    this.final = new FinalPass(this.bloom, transparent);
    this.final.setFxaa(fxaa);
    this.composer.addPass(this.final);
    this.resize(width, height);
  }

  /**
   * The most MSAA samples a half-float (RGBA16F) scene target can take. MAX_SAMPLES is the limit for the formats the
   * GPU renders best; a float format can have a lower one, and a sample count above it fails the renderbuffer
   * allocation (an incomplete target draws nothing). Asked per format where WebGL2 allows it; falls back to
   * MAX_SAMPLES when the query is unavailable.
   */
  static maxHalfFloatSamples(renderer: THREE.WebGLRenderer): number {
    const caps = renderer.capabilities as unknown as { isWebGL2?: boolean; maxSamples?: number };
    if (caps.isWebGL2 === false) return 0;
    const generic = caps.maxSamples ?? 0;
    const gl = renderer.getContext() as WebGL2RenderingContext;
    if (typeof gl.getInternalformatParameter !== "function") return generic;
    try {
      const counts = gl.getInternalformatParameter(gl.RENDERBUFFER, gl.RGBA16F, gl.SAMPLES) as Int32Array | null;
      if (!counts) return generic;
      let most = 0;                                          // the list is descending, but do not rely on it
      for (const n of counts) most = Math.max(most, n);
      return Math.min(generic || most, most);
    } catch {
      return generic;
    }
  }

  /** Applies a look's post settings (bloom, AO on/off, grade, tone mapping). */
  setLook(look: Look, allowAo = true): void {
    this.bloom.strength = look.bloom.strength; this.bloom.radius = look.bloom.radius; this.bloom.threshold = look.bloom.threshold;
    this.bloom.knee = look.bloom.knee ?? 0.01;
    const wantAo = allowAo && look.ao.enabled;
    if (wantAo && !this.ao) {
      const dpr = this.renderer.getPixelRatio() * this.renderScale;
      this.ao = new GTAOPass(this.scene, this.camera, this.width * dpr, this.height * dpr);
      this.ao.output = GTAOPass.OUTPUT.Default;
      PostFx.keepVfxOutOfAo(this.ao);
      this.composer.insertPass(this.ao, 1);
    } else if (!wantAo && this.ao) {
      this.composer.removePass(this.ao); this.ao.dispose(); this.ao = null;
    }
    if (this.ao) {
      this.ao.updateGtaoMaterial({ radius: look.ao.radius, distanceExponent: 1, thickness: 1, scale: look.ao.intensity, samples: 12, distanceFallOff: 1, screenSpaceRadius: false });
      this.ao.blendIntensity = Math.min(1, look.ao.intensity / 1.4);
    }
    this.setGrade(look.grade);
  }

  /** Only the grade (no bloom or AO change): what a look blend (SceneRig.blendLook) drives every frame. */
  setGrade(grade: Look["grade"]): void { this.final.setGrade(grade); }

  /** A bloom override for look blends and the chest stage; `setLook` restores the look's own values. */
  setBloomParams(strength: number, threshold: number, knee?: number, radius?: number): void {
    this.bloom.strength = strength; this.bloom.threshold = threshold;
    if (knee !== undefined) this.bloom.knee = knee;
    if (radius !== undefined) this.bloom.radius = radius;
  }

  /**
   * The reward chest's tone and bloom (look.ts CHEST_TONE): call once after building its renderer and PostFx. With
   * `post` null (reduced effects) only the exposure applies.
   */
  static applyChestTone(renderer: THREE.WebGLRenderer, post: PostFx | null): void {
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = CHEST_TONE.exposure;
    const b = CHEST_TONE.bloom;
    post?.setBloomParams(b.strength, b.threshold, b.knee, b.radius);
  }

  // ---------------------------------------------------------------------------------------------------------------
  // Exposure punch
  // ---------------------------------------------------------------------------------------------------------------

  private static punchPeak = 1;
  private static punchStart = 0;
  private static punchMs = 0;

  /**
   * A one-shot exposure spike that decays back (the strike, a boss slam, a big impact — lighting review §4.2's "+0.6
   * exposure for a frame, then fade"): exposure × `gain` at once, easing out to × 1 over `ms`. Static so any system
   * can call it without a handle to the rig (`PostFx.exposurePunch(1.45, 180)`); it applies to whatever renders next —
   * the post chain's final pass, or the renderer directly on tiers without post (SceneRig.render). A new punch
   * replaces the current one only if it is brighter at this instant. Allocation-free; costs one multiply a frame.
   * Respect reduced effects at the call site (as with trauma).
   */
  static exposurePunch(gain: number, ms: number, now: number = performance.now()): void {
    if (!(gain > 1) || !(ms > 0)) return;
    if (PostFx.punchGain(now) >= gain) return;
    PostFx.punchPeak = gain; PostFx.punchStart = now; PostFx.punchMs = ms;
  }

  /** The punch multiplier at `now` (1 when none): quadratic ease-out from the peak. */
  static punchGain(now: number = performance.now()): number {
    if (PostFx.punchMs <= 0) return 1;
    const k = (now - PostFx.punchStart) / PostFx.punchMs;
    if (k >= 1 || k < 0) { if (k >= 1) PostFx.punchMs = 0; return 1; }
    const e = 1 - k;
    return 1 + (PostFx.punchPeak - 1) * e * e;
  }

  /**
   * The 3D LUT hook (lighting review F6): a 32³ (or any N³) `Data3DTexture` in display-referred sRGB, applied after
   * tone mapping, blended in by `mix` (0 = off, the default; the whole LUT branch is compiled out until a LUT is set).
   * Phase 2's per-biome grades and the "boss dusk" crossfade go through here: author the LUT from the Kingshot refs,
   * set it once, then animate `mix`.
   */
  setLut(lut: THREE.Data3DTexture | null, mix = 1): void { this.final.setLut(lut, mix); }
  setLutMix(mix: number): void { this.final.setLutMix(mix); }

  /**
   * Keeps the additive VFX out of the AO pass's depth/normal prepass.
   *
   * GTAOPass renders the whole scene with `MeshNormalMaterial` as an override to build its depth and normal buffers,
   * and its own `overrideVisibility` only hides points and lines. So a lightning bolt — a camera-facing strip with a
   * `position`/`uv` geometry, no normals, additive, `depthWrite: false` — was written into that prepass as an opaque
   * surface hanging in mid-air with a garbage normal. AO then saw a face fully occluded by nothing and multiplied its
   * silhouette to black: the bolt from the base rendered as a solid black jagged beam, which is exactly what it was —
   * an ambient-occlusion shadow shaped like the bolt, not the bolt itself. (I previously blamed the strike's scorch
   * decal for this; that was wrong.)
   *
   * The rule is general: anything that does not write depth in the beam pass has no business writing depth here.
   */
  private static keepVfxOutOfAo(ao: GTAOPass): void {
    const pass = ao as unknown as { overrideVisibility: () => void };
    const inherited = pass.overrideVisibility.bind(ao);
    pass.overrideVisibility = () => {
      inherited();
      const cache = (ao as unknown as { _visibilityCache: Map<THREE.Object3D, boolean> })._visibilityCache;
      (ao as unknown as { scene: THREE.Scene }).scene.traverse((o) => {
        const mat = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
        if (!mat) return;
        const mats = Array.isArray(mat) ? mat : [mat];
        if (!mats.some((m) => m.transparent && m.depthWrite === false)) return;
        if (!cache.has(o)) cache.set(o, o.visible);
        o.visible = false;
      });
    };
  }

  private width = 1; private height = 1;
  /** Scene-target resolution relative to the canvas (dynamic resolution); the final pass upsamples. */
  private renderScale = 1;
  /**
   * Bloom's blur chain runs at this fraction of the scene target (the tier's `bloom`: at 0.5 it costs a quarter and a
   * glow is soft anyway); 0 turns bloom off, and the final pass then skips its bloom read.
   */
  setBloomScale(scale: number): void {
    this.bloom.enabled = scale > 0;
    if (scale > 0 && scale !== this.bloom.scale) { this.bloom.scale = scale; this.resize(this.width, this.height); }
  }
  setRenderScale(scale: number): void { if (scale !== this.renderScale) { this.renderScale = scale; this.resize(this.width, this.height); } }
  /** FXAA in the final pass (tiers without MSAA): a define, so it switches without a rebuild. */
  setFxaa(on: boolean): void { this.final.setFxaa(on); }

  resize(width: number, height: number): void {
    this.width = width; this.height = height;
    const dpr = this.renderer.getPixelRatio() * this.renderScale;
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(width, height);
    this.ao?.setSize(width * dpr, height * dpr);
  }

  render(): void { this.composer.render(); }

  dispose(): void { this.ao?.dispose(); this.bloom.dispose(); this.final.dispose(); this.composer.dispose(); }
}

/**
 * The one output pass: bloom add, optional FXAA, grade, vignette, exposure + tone mapping, optional 3D LUT, sRGB.
 *
 * Tone mapping follows `renderer.toneMapping` like the OutputPass it replaces (the chest stage keeps its own ACES
 * renderer; SceneRig sets the look's). Supports ACES Filmic, Neutral (Khronos PBR Neutral) and none.
 *
 * The grade (split tone, saturation, contrast, lift) runs on scene-referred linear light before exposure, as before,
 * but the split-tone WEIGHT is display-referred (lighting review F1): it used to be `smoothstep(0.05, 0.9, lum)` on
 * linear luminance, and sunlit grass sits at 0.12–0.2 linear, so the whole meadow counted as "shadow" and was
 * multiplied by the cool shadow tint — the largest of the three blue sources that made the grass emerald-cyan.
 * `lum / (lum + 0.25)` is a Reinhard-style display estimate; the weight now reaches the highlights by mid-grey.
 */
class FinalPass extends Pass {
  private readonly material: THREE.RawShaderMaterial;
  private readonly quad: FullScreenQuad;
  private readonly u: Record<string, THREE.IUniform>;
  private toneMapping: THREE.ToneMapping | null = null;
  private outputColorSpace: string | null = null;
  private fxaa = false;
  /** Whether the compiled defines include the bloom read (bloom switches on and off with the tier). */
  private bloomOn: boolean | null = null;
  private lut: THREE.Data3DTexture | null = null;

  constructor(private readonly bloom: BloomMips, private readonly transparent: boolean) {
    super();
    this.needsSwap = false;
    this.u = {
      tDiffuse: { value: null }, tBloom: { value: null }, tLut: { value: null }, uLutMix: { value: 0 }, uLutSize: { value: 32 },
      toneMappingExposure: { value: 1 }, uTexel: { value: new THREE.Vector2(1, 1) },
      contrast: { value: 1 }, saturation: { value: 1 }, vignette: { value: 0 }, lift: { value: 0 },
      shadows: { value: new THREE.Color(0xffffff) }, highlights: { value: new THREE.Color(0xffffff) },
      uTint: { value: new THREE.Vector4() },
    };
    this.material = new THREE.RawShaderMaterial({
      name: "TdFinalPass", uniforms: this.u, depthTest: false, depthWrite: false, glslVersion: THREE.GLSL3,
      vertexShader: FinalPass.vertex, fragmentShader: FinalPass.fragment,
    });
    this.quad = new FullScreenQuad(this.material);
  }

  setGrade(g: { contrast: number; saturation: number; vignette: number; shadows: number; highlights: number; lift: number }): void {
    const u = this.u;
    u.contrast.value = g.contrast; u.saturation.value = g.saturation; u.vignette.value = g.vignette; u.lift.value = g.lift;
    (u.shadows.value as THREE.Color).set(g.shadows); (u.highlights.value as THREE.Color).set(g.highlights);
  }
  setFxaa(on: boolean): void { if (on !== this.fxaa) { this.fxaa = on; this.toneMapping = null; } }
  setLut(lut: THREE.Data3DTexture | null, mix: number): void {
    if ((lut === null) !== (this.lut === null)) this.toneMapping = null;     // rebuild defines
    this.lut = lut;
    this.u.tLut.value = lut; this.u.uLutMix.value = lut ? mix : 0; this.u.uLutSize.value = lut ? lut.image.width : 32;
  }
  setLutMix(mix: number): void { this.u.uLutMix.value = this.lut ? mix : 0; }

  override setSize(width: number, height: number): void { (this.u.uTexel.value as THREE.Vector2).set(1 / Math.max(1, width), 1 / Math.max(1, height)); }

  override render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget): void {
    this.u.tDiffuse.value = readBuffer.texture;
    this.u.tBloom.value = this.bloom.enabled ? this.bloom.texture : null;
    this.u.toneMappingExposure.value = renderer.toneMappingExposure * PostFx.punchGain();
    // M44: the hit flash and the last stand's vignette (render/screenTint.ts), on the battle's canvas only
    if (!this.transparent) { const t = screenTint.uniforms(performance.now()); (this.u.uTint.value as THREE.Vector4).set(t.stand, t.depth, t.beat, t.hurt); }
    if (this.toneMapping !== renderer.toneMapping || this.outputColorSpace !== renderer.outputColorSpace || this.bloomOn !== this.bloom.enabled) {
      this.toneMapping = renderer.toneMapping; this.outputColorSpace = renderer.outputColorSpace; this.bloomOn = this.bloom.enabled;
      const d: Record<string, string> = {};
      if (THREE.ColorManagement.getTransfer(this.outputColorSpace as THREE.ColorSpace) === THREE.SRGBTransfer) d.SRGB_TRANSFER = "";
      if (this.toneMapping === THREE.ACESFilmicToneMapping) d.ACES_FILMIC_TONE_MAPPING = "";
      else if (this.toneMapping === THREE.NeutralToneMapping) d.NEUTRAL_TONE_MAPPING = "";
      else if (this.toneMapping === THREE.AgXToneMapping) d.AGX_TONE_MAPPING = "";
      if (this.bloom.enabled) d.USE_BLOOM = "";
      if (this.fxaa) d.USE_FXAA = "";
      if (this.lut) d.USE_LUT = "";
      if (this.transparent) d.KEEP_ALPHA = "";
      this.material.defines = d;
      this.material.needsUpdate = true;
    }
    if (this.renderToScreen) renderer.setRenderTarget(null);
    else { renderer.setRenderTarget(writeBuffer); if (this.clear) renderer.clear(); }
    this.quad.render(renderer);
  }

  override dispose(): void { this.material.dispose(); this.quad.dispose(); }

  private static readonly vertex = /* glsl */ `
    precision highp float;
    uniform mat4 modelViewMatrix; uniform mat4 projectionMatrix;
    in vec3 position; in vec2 uv;
    out vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

  // GLSL 3 (a RawShaderMaterial is GLSL ES 1.0 unless told otherwise, and the LUT needs sampler3D). No backticks inside:
  // this is a template literal. The tone-mapping functions and toneMappingExposure come from three's own chunk, so ACES
  // here is bit-identical to what the renderer applies when post is off.
  private static readonly fragment = /* glsl */ `
    precision highp float;
    precision highp sampler3D;
    uniform sampler2D tDiffuse;
    uniform sampler2D tBloom;
    uniform sampler3D tLut;
    uniform float uLutMix, uLutSize;
    uniform vec2 uTexel;
    uniform float contrast, saturation, vignette, lift;
    uniform vec3 shadows, highlights;
    uniform vec4 uTint;   // M44 screen washes: last-stand layer opacity, its depth, the heartbeat's opacity, the hit flash's
    #include <tonemapping_pars_fragment>
    #include <colorspace_pars_fragment>
    in vec2 vUv;
    out vec4 fragColor;

    #ifdef USE_FXAA
    // FXAA folded into the read (medium tiers, where MSAA is off): the classic 5-tap edge search plus two/four taps
    // along the edge. Edge luma is taken on a display estimate (x / (1 + x)) so a bright glow does not turn every
    // neighbour into an edge.
    float fxLuma(vec3 c) { float l = dot(c, vec3(0.299, 0.587, 0.114)); return l / (1.0 + l); }
    vec4 fxaa(vec2 uv) {
      vec4 m = texture(tDiffuse, uv);
      float lNW = fxLuma(texture(tDiffuse, uv + vec2(-1.0, -1.0) * uTexel).rgb);
      float lNE = fxLuma(texture(tDiffuse, uv + vec2( 1.0, -1.0) * uTexel).rgb);
      float lSW = fxLuma(texture(tDiffuse, uv + vec2(-1.0,  1.0) * uTexel).rgb);
      float lSE = fxLuma(texture(tDiffuse, uv + vec2( 1.0,  1.0) * uTexel).rgb);
      float lM = fxLuma(m.rgb);
      float lMin = min(lM, min(min(lNW, lNE), min(lSW, lSE)));
      float lMax = max(lM, max(max(lNW, lNE), max(lSW, lSE)));
      if (lMax - lMin < max(0.0312, lMax * 0.125)) return m;
      vec2 dir = vec2(-((lNW + lNE) - (lSW + lSE)), (lNW + lSW) - (lNE + lSE));
      float reduce = max((lNW + lNE + lSW + lSE) * 0.03125, 1.0 / 128.0);
      float rcpMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + reduce);
      dir = clamp(dir * rcpMin, vec2(-8.0), vec2(8.0)) * uTexel;
      vec3 a = 0.5 * (texture(tDiffuse, uv + dir * (1.0 / 3.0 - 0.5)).rgb + texture(tDiffuse, uv + dir * (2.0 / 3.0 - 0.5)).rgb);
      vec3 b = a * 0.5 + 0.25 * (texture(tDiffuse, uv - dir * 0.5).rgb + texture(tDiffuse, uv + dir * 0.5).rgb);
      float lB = fxLuma(b);
      return vec4((lB < lMin || lB > lMax) ? a : b, m.a);
    }
    #endif

    void main() {
      #ifdef USE_FXAA
        vec4 c = fxaa(vUv);
      #else
        vec4 c = texture(tDiffuse, vUv);
      #endif
      #ifdef USE_BLOOM
        vec4 bl = texture(tBloom, vUv);
        c.rgb += bl.rgb;
        #ifdef KEEP_ALPHA
          c.a = clamp(c.a + bl.a, 0.0, 1.0);
        #endif
      #endif
      vec3 x = max(c.rgb, 0.0);
      float lum = dot(x, vec3(0.2126, 0.7152, 0.0722));
      float w = smoothstep(0.0, 0.35, lum / (lum + 0.25));   // display-referred split-tone weight (see class doc)
      x *= mix(shadows, highlights, w);
      float l2 = dot(x, vec3(0.2126, 0.7152, 0.0722));
      x = mix(vec3(l2), x, saturation);
      x = (x - 0.18) * contrast + 0.18 + lift;
      float d = distance(vUv, vec2(0.5, 0.52)) * 1.35;
      x *= 1.0 - vignette * smoothstep(0.45, 1.15, d);
      x = max(x, 0.0);

      #if defined( ACES_FILMIC_TONE_MAPPING )
        x = ACESFilmicToneMapping(x);
      #elif defined( NEUTRAL_TONE_MAPPING )
        x = NeutralToneMapping(x);
      #elif defined( AGX_TONE_MAPPING )
        x = AgXToneMapping(x);
      #endif

      vec4 o = vec4(x, c.a);
      #ifdef SRGB_TRANSFER
        o = sRGBTransferOETF(o);
      #endif
      #ifdef USE_LUT
        // LUT in display (sRGB-encoded) space, sampled at texel centres.
        vec3 lc = clamp(o.rgb, 0.0, 1.0) * ((uLutSize - 1.0) / uLutSize) + 0.5 / uLutSize;
        o.rgb = mix(o.rgb, texture(tLut, lc).rgb, uLutMix);
      #endif
      // M44: the red screen-edge washes that were DOM layers over the canvas (render/screenTint.ts), composited here in
      // display space as the page composited them. Radii as CSS "ellipse closest-side at center" (1 at the middle of
      // each edge); the hit flash's "ellipse at center" is farthest-corner, i.e. sqrt(2) wider.
      if (uTint.x + uTint.w > 0.0) {
        float r = length((vUv - 0.5) * 2.0);
        // .last-stand: rgba(200,20,16, 0 → 0.62) from 72 % - 16 % * depth to 100 %; .beat over it: rgba(255,30,24, 0 → 0.7) from 62 %
        float s1 = 0.72 - 0.16 * uTint.y;
        float a1 = 0.62 * clamp((r - s1) / (1.0 - s1), 0.0, 1.0);
        float a2 = uTint.z * 0.7 * clamp((r - 0.62) / 0.38, 0.0, 1.0);
        vec3 pm = vec3(1.0, 30.0 / 255.0, 24.0 / 255.0) * a2 + vec3(200.0, 20.0, 16.0) / 255.0 * a1 * (1.0 - a2);
        float aa = a2 + a1 * (1.0 - a2);
        o.rgb = o.rgb * (1.0 - uTint.x * aa) + uTint.x * pm;
        // .hurt-flash: rgba(255,40,30, 0 → 0.55) from 55 % to 100 %, at the flash's opacity
        float a3 = uTint.w * 0.55 * clamp((r * 0.70710678 - 0.55) / 0.45, 0.0, 1.0);
        o.rgb = mix(o.rgb, vec3(1.0, 40.0 / 255.0, 30.0 / 255.0), a3);
      }
      #ifdef KEEP_ALPHA
        // A transparent canvas is composited PREMULTIPLIED over the page: out = rgb + page * (1 - a). Additive light
        // over clear pixels (the chest burst over the parchment) arrives with rgb far above its alpha, so it was ADDED
        // to an already-bright parchment and every burst went to flat white. Treat emitted light as covering what it is
        // at least as bright as: a >= max(rgb) keeps the premultiplied pair valid, and the burst keeps its colour.
        o.a = clamp(max(o.a, max(o.r, max(o.g, o.b))), 0.0, 1.0);
      #endif
      fragColor = o;
    }`;
}
